import { 
  CanonicalProject, 
  ReellyProjectListItem, 
  ReellyProjectDetail, 
  UnitTypeOption 
} from '../types/canonicalProject';
import { 
  validatePricing, 
  validatePropertyType, 
  validatePaymentPlan, 
  validateImages, 
  validateLocation 
} from './validation-rules';
import { calculateROI } from './roi-calculator';
import { Property } from '../types';

// Helper: Round to nearest 100k (FLOOR as per requirements)
export const roundPrice = (p: number) => {
    if (p === 0) return 0;
    return Math.floor(p / 100000) * 100000;
};

/**
 * CANONICAL ADAPTER
 * 
 * Transforms raw Reelly API responses into a strict CanonicalProject model.
 * It merges shallow list data with deep detail data (if available).
 */

export const adaptToCanonical = (
  listData: ReellyProjectListItem,
  detailData?: ReellyProjectDetail
): CanonicalProject => {
  // Use detail data as primary source if available, fallback to list data
  // Cast to any to handle overlapping properties between list/detail endpoints
  const rp = (detailData || listData) as any;
  const hasDeepData = !!detailData;

  // === PRICING VALIDATION ===
  const pricingVal = validatePricing(rp);
  
  // === PROPERTY TYPE VALIDATION ===
  const typeVal = validatePropertyType(rp);
  
  // === PAYMENT PLAN VALIDATION ===
  const planVal = validatePaymentPlan(rp);
  
  // === LOCATION VALIDATION ===
  const locVal = validateLocation(rp);
  
  // === IMAGES VALIDATION ===
  const images = validateImages(rp);

  // === UNIT TYPES ===
  let unitTypes: UnitTypeOption[] = [];
  
  if (Array.isArray(rp.typical_units) && rp.typical_units.length > 0) {
    unitTypes = rp.typical_units.map((u: any) => {
      const beds = u.bedrooms || 0;
      const typeName = beds === 0 ? 'Studio' : `${beds} Bedroom`;
      const rawPrice = u.from_price_aed || u.price || 0;
      const roundedPrice = roundPrice(rawPrice);
      const fromSize = Math.ceil(u.from_size_sqft || 0);
      
      return {
        type: typeName,
        beds: beds,
        sqft: fromSize > 0 ? `${fromSize.toLocaleString()}` : 'TBD',
        price: roundedPrice > 0 ? `AED ${new Intl.NumberFormat('en-US').format(roundedPrice)}` : 'POA',
        priceNumeric: roundedPrice > 0 ? roundedPrice : null
      };
    });
  } else if (Array.isArray(rp.parkings)) {
    // Fallback units from parkings if typical_units missing
    rp.parkings.forEach((p: any) => {
      if (p.unit_type && Array.isArray(p.unit_bedrooms)) {
        const formatted = p.unit_type.charAt(0).toUpperCase() + p.unit_type.slice(1);
        p.unit_bedrooms.forEach((br: string) => {
          unitTypes.push({
            type: `${formatted} ${br}`,
            beds: parseInt(br) || 0,
            sqft: 'TBD',
            price: 'POA',
            priceNumeric: null
          });
        });
      }
    });
  }

  // === DERIVED: bedroomsAvailable ===
  const bedroomsAvailable = [...new Set(unitTypes.map(u => u.beds))].sort((a, b) => a - b);

  // === DERIVED: completionTimestamp ===
  let completionTimestamp: number | null = null;
  // Priority: completion_datetime > construction_end_date > parse completion_date
  if (rp.completion_datetime) {
    completionTimestamp = new Date(rp.completion_datetime).getTime();
  } else if (rp.construction_end_date) {
    completionTimestamp = new Date(rp.construction_end_date).getTime();
  } else if (rp.completion_date) {
    // Try to parse "Q4 2026", "JAN 2027", "2025", etc.
    const yearMatch = rp.completion_date.match(/\b20\d{2}\b/);
    if (yearMatch) {
      const year = parseInt(yearMatch[0]);
      const qMatch = rp.completion_date.match(/Q([1-4])/);
      if (qMatch) {
        const quarter = parseInt(qMatch[1]);
        completionTimestamp = new Date(year, (quarter - 1) * 3, 1).getTime();
      } else {
        completionTimestamp = new Date(year, 0, 1).getTime();
      }
    }
  }
  if (completionTimestamp && isNaN(completionTimestamp)) completionTimestamp = null;

  // === ROI CALCULATION ===
  const developerName = typeof rp.developer === 'string' 
    ? rp.developer 
    : (rp.developer?.name || 'Unknown Developer');
    
  const minSize = rp.min_size || 0;
  const maxSize = rp.max_size || minSize;
  const avgSqft = minSize > 0 ? Math.ceil((minSize + maxSize) / 2) : 0;
  // Calculate STARTING price per square foot: min_price ÷ min_size (NEW FORMULA)
  // This represents the entry-level unit (smallest size at lowest price)
  const minPrice = rp.min_price || 0;
  const ppsf = minPrice > 0 && minSize > 0 ? Math.round(minPrice / minSize) : 0;
  
  if (import.meta.env.DEV && ppsf > 0) {
    console.log(`[CanonicalAdapter] Calculated PSF: ${ppsf} (min_price: ${minPrice}, min_size: ${minSize})`);
  }
  const roiCalc = calculateROI(locVal.displayText, developerName, ppsf, rp.completion_date);

  // === BADGE LOGIC ===
  let badgeText = rp.sale_status?.toUpperCase() || 'OFF-PLAN';
  let badgeColor = 'blue';

  if (badgeText === 'OUT_OF_STOCK') {
    badgeText = 'SOLD OUT';
    badgeColor = 'slate'; 
  } else if (badgeText === 'ON_SALE') {
    badgeText = 'ON SALE';
    badgeColor = 'green';
  }

  // Override Badge if Payment Plan exists (Canonical Rule)
  let paymentPlanSummary = null;
  let canonicalPaymentPlan = null;

  const badgeUpdatable = ['ON SALE', 'OFF-PLAN', 'ACTIVE'].includes(badgeText);

  if (planVal.plan) {
    const constructionTotal = Math.round(planVal.plan.downPayment + planVal.plan.duringConstruction);
    const handoverTotal = Math.round(planVal.plan.onHandover);
    paymentPlanSummary = `${constructionTotal}/${handoverTotal}`;
    
    canonicalPaymentPlan = {
      ...planVal.plan,
      total: constructionTotal + handoverTotal
    };

    // Only update badge if it's currently a generic/on-sale state
    if (badgeUpdatable) {
      badgeText = paymentPlanSummary;
      badgeColor = 'blue';
    }
  } else if (hasDeepData) {
    // If we have deep data but no plan, explicitly show TBD
    paymentPlanSummary = 'Plan TBD';
    if (badgeUpdatable) {
      badgeText = 'Plan TBD';
      badgeColor = 'slate';
    }
  }

  // Normalize pricing source to match CanonicalProject type
  let pricingSource: 'unit_inventory' | 'api_min_price' | 'unavailable' = 'unavailable';
  if (pricingVal.source === 'unit_prices') pricingSource = 'unit_inventory';
  else if (pricingVal.source === 'min_price') pricingSource = 'api_min_price';

  const result: CanonicalProject = {
    id: String(rp.id),
    title: rp.name,
    developer: developerName,
    developerLogo: rp.developer?.logo || undefined,
    
    location: {
      display: locVal.displayText,
      district: rp.location?.district,
      city: rp.location?.city,
      region: rp.location?.region,
      coordinates: locVal.coordinates ? {
        ...locVal.coordinates,
        isGeneric: locVal.isGeneric
      } : undefined
    },

    pricing: {
      startingPriceNumeric: pricingVal.priceNumeric > 0 ? pricingVal.priceNumeric : null,
      startingPriceExact: pricingVal.priceNumeric, // We don't have exact raw min from list always
      minPrice: rp.min_price || null,
      maxPrice: rp.max_price || null,
      startingPriceDisplay: pricingVal.priceDisplay,
      currency: 'AED',
      source: pricingSource,
      isRounded: pricingVal.isRounded,
      hasDeepData: hasDeepData
    },

    size: {
      minSqft: rp.min_size ? Math.ceil(rp.min_size) : null,
      maxSqft: rp.max_size ? Math.ceil(rp.max_size) : null,
      avgSqft: avgSqft || null,
      display: avgSqft > 0 ? `${Math.ceil(avgSqft).toLocaleString()} sqft` : 'TBD'
    },

    pricePerSqft: {
      low: ppsf || null,  // Starting price per sqft: min_price ÷ min_size
      high: (rp.max_price && rp.min_size) ? Math.round(rp.max_price / rp.min_size) : null,  // High end: max_price ÷ min_size
      estimated: ppsf || null,  // Same as low (starting price per sqft)
      display: ppsf > 0 ? `AED ${ppsf.toLocaleString()}/sqft` : 'TBD'
    },

    propertyType: {
      primary: typeVal.type,
      types: typeVal.type.includes('&') ? typeVal.type.split(' & ') : [typeVal.type],
      source: typeVal.source,
      isDefaulted: typeVal.isDefaulted
    },

    unitTypes: unitTypes,
    bedroomsAvailable: bedroomsAvailable,

    paymentPlan: canonicalPaymentPlan,
    paymentPlanSummary: paymentPlanSummary,

    status: {
      sale: rp.sale_status || 'unknown',
      construction: rp.construction_status || 'under_construction',
      completion: rp.completion_date || 'TBD',
      completionTimestamp: completionTimestamp
    },

    media: {
      coverImage: images[0],
      gallery: images,
      hasFloorPlans: Array.isArray(rp.floor_plans) && rp.floor_plans.length > 0,
      hasBrochure: !!rp.marketing_brochure,
      brochureUrl: typeof rp.marketing_brochure === 'string' ? rp.marketing_brochure : rp.marketing_brochure?.url
    },

    content: {
      description: rp.overview || '',
      aboutOpportunity: rp.ai_about_opportunity || null,
      investmentSummary: rp.short_description || "Prime investment opportunity sourced via Reelly API.",
      features: Array.isArray(rp.project_amenities) ? rp.project_amenities.map(a => a.amenity?.name || a.name).filter(Boolean).slice(0, 8) : []
    },

    roi: {
      projected: roiCalc.roiValue || null,
      display: roiCalc.roi,
      trend: roiCalc.trend as any,
      roiData: []
    },

    metadata: {
      createdAt: rp.created_at || new Date().toISOString(),
      updatedAt: rp.updated_at || new Date().toISOString(),
      lastSeen: new Date().toISOString(),
      unitsCount: rp.units_count || 0,
      isMasterProject: rp.units_count === 0 && (rp.name.includes('Valley') || rp.name.includes('Oasis')),
      escrowNumber: rp.escrow_number
    },

    dataQuality: {
      hasDeepData: hasDeepData,
      isPriceVerified: pricingVal.source === 'unit_prices',
      missingFields: [],
      warnings: []
    },

    // Legacy fields
    badge: {
      text: badgeText,
      color: badgeColor as "green" | "blue" | "orange" | "purple" | "slate"
    },
    price: pricingVal.priceDisplay,
    priceNumeric: pricingVal.priceNumeric,
    type: typeVal.type,
    sqft: avgSqft > 0 ? `${avgSqft.toLocaleString()} sqft` : 'TBD',
    sqftNumeric: avgSqft,
    completion: rp.completion_date || 'TBD',
    image: images[0],
    images: images
  };

  return result;
};

/**
 * Maps the Canonical Project model to the Flat legacy "Property" model
 * used by the UI components throughout the application.
 */
export const adaptCanonicalToProperty = (cp: CanonicalProject): Property => {
  return {
    id: cp.id,
    slug: cp.slug, // Pass through slug for SEO-friendly URLs
    title: cp.title,
    location: cp.location.display,
    price: cp.pricing.startingPriceDisplay,
    priceRaw: cp.pricing.startingPriceExact || cp.pricing.startingPriceNumeric || 0,
    priceNumeric: cp.pricing.startingPriceNumeric || 0,
    pricePerSqft: cp.pricePerSqft.estimated || 0,
    sqftNumeric: cp.size.avgSqft || 0,
    minSqft: cp.size.minSqft || null,
    roi: cp.roi.display,
    roiNumeric: cp.roi.projected || 0,
    roiTrend: cp.roi.trend === 'UP' ? 'up' : cp.roi.trend === 'DOWN' ? 'down' : 'flat',
    completion: cp.status.completion,
    image: cp.media.coverImage,
    developer: cp.developer,
    developerLogo: cp.developerLogo || '',
    createdAt: cp.metadata.createdAt,
    badge: {
      text: cp.badge.text,
      color: cp.badge.color as "green" | "blue" | "orange" | "purple" | "slate"
    },
    // Optional / Details
    description: cp.content.description,
    aiAboutOpportunity: cp.content.aboutOpportunity || undefined,
    investmentSummary: cp.content.investmentSummary,
    type: cp.propertyType.primary,
    unitTypes: cp.unitTypes,
    bedroomsAvailable: cp.bedroomsAvailable,
    completionTimestamp: cp.status.completionTimestamp,
    features: cp.content.features,
    images: cp.media.gallery,
    coordinates: cp.location.coordinates,
    roiData: cp.roi.roiData as any, // Cast or map if needed
    paymentPlan: cp.paymentPlan ? {
        downPayment: cp.paymentPlan.downPayment,
        duringConstruction: cp.paymentPlan.duringConstruction,
        onHandover: cp.paymentPlan.onHandover
    } : undefined,
    lastUpdated: cp.metadata.updatedAt,
    isMasterProject: cp.metadata.isMasterProject,
    brochureUrl: cp.media.brochureUrl,
    floor_plans: cp.media.floor_plans,
    master_plan: cp.media.master_plan || undefined
  };
};
